<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { AUTH_PROVIDERS } from './authProviders'
import EmailExistsModal from './modals/EmailExistsModal.vue'
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js/min'
import ConsentAdvertisingModal from '../layout/ConsentAdvertisingModal.vue'
import { useScreen } from '@/composables/useScreen'
import { useNotification } from '@kyvg/vue3-notification'
import { useLoginSection, type LoginSectionProps } from './useLoginSection'
import { httpPost } from '@/lib/http'
import { mapKeyValToOption } from '@/helpers/dataMappers'
import { currentUser } from '@/lib/currentUser'
import { getIntlState } from '@/lib/intl'

const props = defineProps<LoginSectionProps>()

const emit = defineEmits<{
  (e: 'change', section: string): void
  (e: 'update:shared-data', data: Record<string, unknown>): void
}>()

const { changeSection, setDataItem } = useLoginSection(props, emit)

const { isMobile } = useScreen()
const { notify } = useNotification()
const consentModal = ref<{ $open: () => void } | null>(null)

const form = reactive<Record<string, unknown>>({
  agreeWithPolicy: false,
  agreeWithOferta: false,
  consent_to_advertising: false
})
const errors = reactive<Record<string, string>>({})
const disabledFields = ref<string[]>([])
const submitting = ref(false)
const emailExists = ref(false)
const skipConsentCheck = ref(false)

onMounted(() => {
  if (props.sharedData.credentials) {
    const { email, password } = props.sharedData.credentials
    form.email = email
    form.password = password
  }

  const prefillEntries = Object.entries(props.context.prefill || {})
  for (const [key, item] of prefillEntries as Array<[string, { value: string; editable: boolean }]>) {
    if (!item.editable) disabledFields.value.push(key)
    if (item.value) form[key] = item.value
  }

  if (!getIntlState()) {
    form.country = props.context.countries?.[0]?.[0] ?? ''
  }
})

const countriesList = computed(() => (props.context.countries ?? []).map(mapKeyValToOption))
const showBackButton = computed(() => !props.query.has('type'))
const oauthProviderInfo = computed(() => {
  const oauth = props.context.oauth
  if (!oauth) return null
  const provider = AUTH_PROVIDERS.find(item => item.serverSlug === oauth.provider)
  if (!provider) return null
  return { icon: `auth-providers/${provider.slug}`, label: provider.label, username: oauth.provider_user_name }
})

const authUrls = computed(() => {
  const isIntl = getIntlState()
  return AUTH_PROVIDERS.filter(item => !(item.ruOnly && isIntl) && item.available !== false).map(item => {
    const url = new URL(`/users/auth_${item.slug}`, window.location.href)
    const params = new URLSearchParams(window.location.search)
    params.set('type', props.sharedData.role === 1 ? 'teacher' : 'parent')
    url.search = params.toString()
    return { key: item.slug, url: url.toString() }
  })
})

const fields = computed(() => {
  const result: Array<{
    key: string
    label: string
    type?: string
    placeholder?: string
    autocomplete?: string
    hint?: string
    items?: unknown[]
    mask?: string
  }> = [
    { key: 'last_name', label: 'Фамилия', placeholder: 'Введите фамилию', autocomplete: 'family-name' },
    { key: 'first_name', label: 'Имя', placeholder: 'Введите имя', autocomplete: 'given-name' },
    { key: 'country', type: 'select', items: countriesList.value, label: 'Страна' }
  ]

  if (props.query.get('create_step') !== 'true') {
    if (props.query.get('password') !== 'false') {
      result.unshift({
        key: 'password',
        type: 'password',
        label: 'Пароль',
        placeholder: 'Придумайте пароль',
        autocomplete: 'new-password'
      })
    }
    result.unshift({
      key: 'email',
      type: 'email',
      label: 'Эл. почта',
      placeholder: 'Введите вашу почту',
      autocomplete: 'email'
    })
  }

  if (props.sharedData.role === 1) {
    result.push({
      key: 'phone_number',
      type: 'tel',
      label: 'Номер телефона',
      placeholder: 'Введите номер телефона',
      hint: 'Необходим для связи с кураторами курсов и семинаров'
    })
  }

  return result
})

const trustedEmailPrefilled = computed(() => props.context.prefill?.email?.trusted)

async function submit() {
  const payload = { ...form }
  Object.keys(errors).forEach(key => delete errors[key])

  if (!payload.agreeWithPolicy) errors.agreeWithPolicy = 'Обязательное поле для заполнения.'
  if (!payload.agreeWithOferta) errors.agreeWithOferta = 'Обязательное поле для заполнения.'

  if (!payload.consent_to_advertising && !skipConsentCheck.value) {
    consentModal.value?.$open()
    return
  }

  if (props.sharedData.role === 1 && !isValidPhoneNumber(String(payload.phone_number || ''), 'RU')) {
    errors.phone_number = 'Некорректный номер телефона.'
  }
  if (Object.keys(errors).length) return

  submitting.value = true

  if (payload.phone_number) {
    payload.phone_number = parsePhoneNumber(String(payload.phone_number), 'RU').format('E.164')
  }
  if (props.query.has('signup_data_id')) {
    payload.signup_data_id = props.query.get('signup_data_id')
  }

  try {
    const resp = await httpPost<{
      failed?: boolean
      message?: string
      user?: { id: number; first_name: string; last_name: string }
      errors?: Record<string, Array<{ code: string; message: string }>>
    }>('/users/request_signup', payload)
    submitting.value = false

    if (!resp.failed && !Object.keys(errors).length) {
      setDataItem('email', payload.email)
      if (resp.user) {
        currentUser.id = resp.user.id
        currentUser.firstName = resp.user.first_name
        currentUser.lastName = resp.user.last_name
      }
      changeSection(props.LoginSection.EmailVerification)
      return
    }

    if (resp.failed && resp.message) throw resp.message

    for (const key in resp.errors) {
      errors[key] = resp.errors[key][0].message
    }

    if (resp.failed && resp.errors?.email?.[0]?.code === 'unique') {
      emailExists.value = true
    }
  } catch (err) {
    let title = 'Произошла неизвестная ошибка при регистрации. Повторите попытку позже.'
    if (typeof err === 'string') title = err
    notify({ type: 'error', title })
    submitting.value = false
  }
}

function switchVisibility(event: MouseEvent) {
  const btn = event.currentTarget as HTMLElement
  const input = btn.previousElementSibling as HTMLInputElement
  if (!input) return

  if (input.type === 'password') {
    input.type = 'text'
    btn.classList.remove('signup-profile__form-inactive')
  } else {
    input.type = 'password'
    btn.classList.add('signup-profile__form-inactive')
  }
}

function goBack() {
  setDataItem('role', undefined)
  changeSection(props.LoginSection.RoleSelect)
}

function clearError(key: string) {
  delete errors[key]
}
</script>

<template>
  <div class="signup-profile">
    <EmailExistsModal v-model="emailExists" :email="String(form.email || '')" :oauth-provider="oauthProviderInfo" />
    <ConsentAdvertisingModal
      ref="consentModal"
      manual
      @accept="
        val => {
          form.consent_to_advertising = val
          skipConsentCheck = true
          submit()
        }
      "
    />

    <template v-if="trustedEmailPrefilled">
      <h1 class="text-h3 text-center mb-2">Заполните профиль</h1>
    </template>
    <template v-else>
      <h1 class="text-h3 text-center mb-2">Создать аккаунт</h1>

      <div v-if="oauthProviderInfo" class="signup-oauth-badge mb-4">
        <v-icon class="signup-oauth-badge__icon" size="32">
          {{ oauthProviderInfo.icon }}
        </v-icon>

        <span class="signup-oauth-badge__title"> Регистрация через {{ oauthProviderInfo.label }} </span>

        <span class="signup-oauth-badge__username" v-text="oauthProviderInfo.username" />
      </div>
      <template v-else>
        <ul class="signin-form__oauth mt-4">
          <li v-for="provider in authUrls" :key="provider.key">
            <a :href="provider.url">
              <v-icon size="32">auth-providers/{{ provider.key }}</v-icon>
            </a>
          </li>
        </ul>

        <div class="signin-form__divider my-3">
          <hr />
          <span class="mx-1">Или</span>
          <hr />
        </div>
      </template>
    </template>

    <div class="signup-profile__form">
      <v-field v-for="field in fields" :key="field.key">
        <template #label>
          <div class="d-flex justify-space-between">
            {{ field.label }}

            <v-menu-base v-if="field.hint" no-mobile-modal :pos-horizontal="isMobile ? 'right' : 'center'">
              <template #activator="{ onclick, onmouseenter, onmouseleave }">
                <div style="cursor: help" @mouseenter="onmouseenter" @mouseleave="onmouseleave" @click="onclick">
                  <v-icon class="gray-text">mdi-information-outline</v-icon>
                </div>
              </template>

              <p class="pa-1" style="width: 280px; white-space: normal">
                {{ field.hint }}
              </p>
            </v-menu-base>
          </div>
        </template>

        <v-select
          v-if="field.type === 'select'"
          v-model="form[field.key]"
          :name="field.key"
          :items="field.items"
          :error="errors[field.key]"
          placeholder="Выберите"
          @change="clearError(field.key)"
        />
        <v-input
          v-else
          v-model.trim="form[field.key]"
          v-maska="field.mask"
          :name="field.key"
          :type="field.type || 'text'"
          :error="errors[field.key]"
          :placeholder="field.placeholder"
          :autocomplete="field.autocomplete"
          :disabled="disabledFields.includes(field.key)"
          @change="clearError(field.key)"
        >
          <template #append>
            <a v-if="field.type === 'password'" class="signup-profile__form-inactive" @click.stop="switchVisibility">
              <v-icon>mdi-eye</v-icon>
            </a>
          </template>
        </v-input>
      </v-field>
    </div>

    <v-checkbox
      v-model="form.agreeWithOferta"
      :error="errors.agreeWithOferta"
      class="mt-3"
      @update:model-value="clearError('agreeWithOferta')"
    >
      <p class="signup-profile__checkbox-links">
        Я ознакомлен(-а) и согласен(-на) с
        <a :href="sharedData.legal?.offer" target="_blank" @click.stop="() => {}">
          <span>правилами использования сайта</span> </a
        ><span class="red-text">*</span>
      </p>
    </v-checkbox>
    <span v-if="errors.agreeWithOferta" class="red-text mt-1" v-text="errors.agreeWithOferta" />

    <v-checkbox
      v-model="form.agreeWithPolicy"
      :error="errors.agreeWithPolicy"
      class="mt-2"
      @update:model-value="clearError('agreeWithPolicy')"
    >
      <p class="signup-profile__checkbox-links">
        Ознакомлен(-а) с условиями
        <a :href="sharedData.legal?.privacy" target="_blank" @click.stop="() => {}">
          <span>Политики конфиденциальности</span>
        </a>
        и даю
        <a :href="sharedData.legal?.processing" target="_blank" @click.stop="() => {}">
          <span>согласие на обработку персональных данных</span> </a
        ><span class="red-text">*</span>
      </p>
    </v-checkbox>
    <span v-if="errors.agreeWithPolicy" class="red-text mt-1" v-text="errors.agreeWithPolicy" />

    <v-checkbox
      v-model="form.consent_to_advertising"
      :error="errors.consent_to_advertising"
      class="mt-2"
      @update:model-value="clearError('consent_to_advertising')"
    >
      <p class="signup-profile__checkbox-links">
        Даю
        <a :href="sharedData.legal?.advertising" target="_blank" @click.stop="() => {}">
          <span>согласие на получение рекламно-информационных материалов</span>
        </a>
      </p>
    </v-checkbox>

    <v-button class="mt-6" mode="primary large block" :disabled="submitting" @click="() => submit()">
      Продолжить
    </v-button>
    <v-button v-if="showBackButton" class="mt-2" mode="outlined large block" :disabled="submitting" @click="goBack">
      Назад
    </v-button>
  </div>
</template>

<style lang="scss">
.signup-profile {
  @include desktop {
    width: 320px !important;
    margin: 0 auto;
  }

  &__form {
    display: grid;
    grid-auto-rows: auto;

    gap: 20px 0;

    .v-input[disabled] {
      background: $lightenGray;
      border-color: $lightGray;
      opacity: 1;
      input {
        color: $black;
      }
    }
  }

  &__form-label {
    display: flex;

    @include desktop {
      flex-direction: column;
      justify-content: center;
    }

    @include mobile {
      margin-top: 12px;
      justify-content: space-between;
      align-items: center;
    }
  }

  &__form-inactive {
    color: $gray;
  }

  &__checkbox-links a {
    color: $blue;
  }
}

.signup-oauth-badge {
  background: $lightenGray;
  border-radius: 8px;
  padding: 8px 16px;

  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto auto;
  grid-auto-columns: 1fr;
  grid-auto-flow: row;
  gap: 4px 16px;
  grid-template-areas:
    'icon title'
    'icon username';

  &__icon {
    grid-area: icon;
    place-self: center;
  }

  &__title {
    grid-area: title;
  }

  &__username {
    grid-area: username;
    font-weight: bold;
  }
}
</style>
